import React from 'react';
import FormInputPhoneNew, { phoneNumberRequiredValidator, phoneNumberValidator } from './FormInputPhoneNew';
import { Form, Button, Container, Row, Col } from 'react-bootstrap';
import { Formik, Field, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import FormSelect from './FormSelect';
import LocationAutocomplete from './LocationAutocomplete';
import { defaultCurrencies, defaultFleetOptions, europeanCountriesCodes, createNewPartner } from '../const';
import './LocationAutocomplete/index.css';

// Validation schema
const validationSchema = Yup.object({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: phoneNumberRequiredValidator().concat(
    phoneNumberValidator()),
  company: Yup.string().required('Required'),
  fleetSize: Yup.string().required('Required'),
  addressLine1: Yup.string().required('Required'),
  website: Yup.string().required('Required'),
  vatNumber: Yup.string(),
  businessNotVatRegistered: Yup.boolean(),
  currency: Yup.object().nullable(),
  country: Yup.string().required('Required'),
  accountNumber: Yup.string().when('currency', {
    is: currency => ['USD', 'GBP', 'AUD'].includes(currency?.label),
    then: Yup.string().required('Account Number is required'),
  }),
  routingNumber:  Yup.string().when('currency', {
    is: currency => currency?.label === 'USD',
    then: Yup.string().required('Routing Number is required'),
  }),
  sortCode: Yup.string().when('currency', {
    is: currency => currency?.label === 'GBP',
    then: Yup.string().required('Sort Code is required'),
  }),
  institutionNumber: Yup.string().when('currency', {
    is: currency => currency?.label === 'CAD',
    then: Yup.string().required('Institution Number is required'),
  }),
  transitNumber: Yup.string().when('currency', {
    is: currency => currency?.label === 'CAD',
    then: Yup.string().required('Transit Number is required'),
  }),
  iban: Yup.string().when('currency', {
    is: currency => currency?.label === 'EUR',
    then: Yup.string().required('iBAN is required'),
  }),
  swiftCode: Yup.string().when('currency', {
    is: currency => currency?.label === 'EUR',
    then: Yup.string().required('Swift code is required'),
  }),
});

export default function RegisterForm() {
  const initialValues = {
    firstName: '', lastName: '', email: '', phone: '',
    company: '', fleetSize: '', addressLine1: '', website: '', vatNumber: '',
    businessNotVatRegistered: false, currency: null, country: '',
    accountNumber: '', routingNumber: '', sortCode: '', institutionNumber: '', transitNumber: '',
    iban: '', swiftCode: ''
  };

  const vatCountries = ['GB', 'AE'];
  const gstCountries = ['AU', 'CA'];
  const jctCountries = ['JP'];
  let chosenLocation = {};
  try {
    const stored = localStorage.getItem('chosenPartnerCity');
    chosenLocation = stored ? JSON.parse(stored) : {};
  } catch {
    chosenLocation = {};
  }
  const countryCode = chosenLocation.country || '';
  const isEurope = europeanCountriesCodes.includes(countryCode);
  const taxLabel =
    (vatCountries.includes(countryCode) || isEurope) ? 'VAT'
    : gstCountries.includes(countryCode) ? 'GST'
    : jctCountries.includes(countryCode) ? 'JCT'
    : 'tax';

    const onSubmit = async (values, form) => {
        try {
            const stored = localStorage.getItem('chosenPartnerCity');
            let item = {
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                name: values.firstName.trim() + ' ' + values.lastName.trim(),
                company: values.company,
                email: values.email,
                phoneNumber: values.phone,
                fleetSize: values.fleetSize.label,
                website: values.website,
                isTaxRegistered: values.businessNotVatRegistered,
                address: values.addressLine1.fullAddress,
                currency: values.currency,
                country: values.country,
                place: JSON.parse(stored),
            }

            if(values.iban){ item.iban = values.iban }
            if(values.swiftCode){ item.swiftCode = values.swiftCode }
            if(values.accountNumber){ item.accountNumber = values.accountNumber }
            if(values.routingNumber){ item.routingNumber = values.routingNumber }
            if(values.sortCode){ item.sortCode = values.sortCode }
            if(values.institutionNumber){ item.institutionNumber = values.institutionNumber }
            if(values.transitNumber){ item.transitNumber = values.transitNumber }

            await createNewPartner(item)
            localStorage.removeItem('chosenPartnerCity');
            window.location = `/thankyou/${item.email}`;
            form.setSubmitting(false)
            return
        } catch (error) {
            console.log(error)
            form.setSubmitting(false)
            return
        }
    }

  return (
    <Container style={{ maxWidth: '800px', marginTop: '20px' }}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting }) => {
          const currLabel = values.currency?.label;
          let bankFields = [];
          if (currLabel === 'USD') {
            bankFields = [
              { name: 'accountNumber', label: 'Account Number*' },
              { name: 'routingNumber', label: 'Routing Number*' }
            ];
          } else if (currLabel === 'GBP') {
            bankFields = [
              { name: 'sortCode', label: 'Sort Code*' },
              { name: 'accountNumber', label: 'Account Number*' }
            ];
          } else if (currLabel === 'CAD') {
            bankFields = [
              { name: 'institutionNumber', label: 'Institution Number*' },
              { name: 'transitNumber', label: 'Transit Number*' }
            ];
          } else {
            bankFields = [
              { name: 'iban', label: 'IBAN*' },
              { name: 'swiftCode', label: 'SWIFT Code*' }
            ];
          }

          return (
            <FormikForm>
              <h4 className="mt-5">Enter your personal information</h4>
              <br></br>
              <br></br>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="firstName">
                    <Form.Label>First Name*</Form.Label>
                    <Field name="firstName" className="form-control" />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="lastName">
                    <Form.Label>Last Name*</Form.Label>
                    <Field name="lastName" className="form-control" />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="email">
                    <Form.Label>Email*</Form.Label>
                    <Field name="email" type="email" className="form-control" />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Field
                    name="phone"
                    component={FormInputPhoneNew}
                    label="Phone Number*"
                    controlId="phone"
                  />
                </Col>
              </Row>

              <hr></hr>

              <h4 className="mt-4">Enter your business information</h4>
              <br></br>
              <br></br>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="company">
                    <Form.Label>Company Name*</Form.Label>
                    <Field name="company" className="form-control" />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="fleetSize">
                    <Form.Label>Fleet Size*</Form.Label>
                    <Field
                      name="fleetSize"
                      component={FormSelect}
                      options={defaultFleetOptions}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="addressLine1">
                    <Form.Label>Address Line 1*</Form.Label>
                    <Field
                      name="addressLine1"
                      component={LocationAutocomplete}
                      needFullAddress
                      displayKey="fullAddress"
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="website">
                    <Form.Label>Website*</Form.Label>
                    <Field name="website" className="form-control" />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="vatNumber">
                    <Form.Label>{taxLabel} Number</Form.Label>
                    <Field name="vatNumber" className="form-control" />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3 d-flex align-items-center">
                  <div className="form-check d-flex align-items-center">
                    <Field
                      type="checkbox"
                      name="businessNotVatRegistered"
                      className="form-check-input"
                      style={{ width: '15px', height: '15px' }}
                    />
                    <Form.Label
                      htmlFor="businessNotVatRegistered"
                      className="form-check-label ml-2"
                      style={{ fontSize: '15px', color: '#000', textTransform: 'none' }}
                    >
                      {`Business not ${taxLabel} registered`}
                    </Form.Label>
                  </div>
                </Col>
              </Row>

              <hr></hr>

              <h4 className="mt-4">Enter your bank information</h4>
              <br></br>
              <br></br>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="currency">
                    <Form.Label>Currency*</Form.Label>
                    <Field
                      name="currency"
                      controlId={'currency'}
                      component={FormSelect}
                      options={defaultCurrencies}
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="country">
                    <Form.Label>Country*</Form.Label>
                    <Field
                      name="country"
                      component={LocationAutocomplete}
                      needFullAddress
                      displayKey="fullAddress"
                      countriesList
                      placeholder="Type country..."
                      searchOptions={{ types: ['administrative_area_level_3', 'political', 'locality'] }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                {bankFields.map(field => (
                  <Col md={6} key={field.name} className="mb-3">
                    <Form.Group controlId={field.name}>
                      <Form.Label>{field.label}</Form.Label>
                      <Field name={field.name} className="form-control" />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
              <div className="d-flex justify-content-end mb-5">
                <Button type="submit" disabled={isSubmitting} className="mt-3 float-right"  style={{ width: '140px', height: '45px', backgroundColor: '#a79779', borderColor: '#a79779', fontSize: '1.1rem' }}>
                    Submit
                </Button>
              </div>
            </FormikForm>
          );
        }}
      </Formik>
    </Container>
  );
}